import React, { useState, useEffect } from 'react';
import { tourEvent, useLessonAction } from '../tour/tourEvents';

// =============================================================================
// AI Column modal
// -----------------------------------------------------------------------------
// Creates a new custom column populated by Ada reviewing each row's in-system
// fields. Mandatory 10-row sample + cost estimate before the full run. On Run
// it kicks off a background job and hands it to the parent (via onJobStarted),
// then closes — the parent owns progress + live cell fill-in from there.
// =============================================================================

type EntityType = 'candidates' | 'companies' | 'managers';
type OutputType = 'category' | 'text' | 'score';

interface SourceField { key: string; label: string; }

// Mirrors AI_SOURCE_FIELDS in backend/services/aiColumns/aiColumnEngine.js
const SOURCE_FIELDS: Record<EntityType, SourceField[]> = {
  companies: [
    { key: 'company_name', label: 'Company name' },
    { key: 'industry', label: 'Industry' },
    { key: 'subindustry', label: 'Subindustry' },
    { key: 'business_type', label: 'Business type' },
    { key: 'business_stage', label: 'Business stage' },
    { key: 'primary_offerings', label: 'Primary offerings' },
    { key: 'specialties', label: 'Specialties' },
    { key: 'description', label: 'Description' },
    { key: 'employee_count', label: 'Employees' },
    { key: 'founded', label: 'Founded' },
    { key: 'headquarters', label: 'HQ' },
    { key: 'city', label: 'City' },
    { key: 'country', label: 'Country' },
    { key: 'website', label: 'Website' },
  ],
  candidates: [
    { key: 'name', label: 'Name' },
    { key: 'current_company', label: 'Current company' },
    { key: 'current_position', label: 'Current title' },
    { key: 'current_position_description', label: 'Current role description' },
    { key: 'headline', label: 'Headline' },
    { key: 'summary', label: 'Profile summary' },
    { key: 'previous_company_1', label: 'Previous company 1' },
    { key: 'previous_position_1', label: 'Previous title 1' },
    { key: 'previous_position_1_description', label: 'Previous role 1 description' },
    { key: 'previous_company_2', label: 'Previous company 2' },
    { key: 'previous_position_2', label: 'Previous title 2' },
    { key: 'previous_position_2_description', label: 'Previous role 2 description' },
    { key: 'previous_company_3', label: 'Previous company 3' },
    { key: 'previous_position_3', label: 'Previous title 3' },
    { key: 'previous_position_3_description', label: 'Previous role 3 description' },
    { key: 'position_history', label: 'All previous roles (full history — more tokens)' },
    { key: 'location', label: 'Location' },
    { key: 'education_1', label: 'Education' },
    { key: 'education_notes', label: 'Education notes' },
    { key: 'education_history', label: 'All education (full history)' },
    { key: 'education_grades', label: 'Education grades' },
    { key: 'degree_1', label: 'Degree' },
    { key: 'all_skills_text', label: 'Skills' },
  ],
  managers: [
    { key: 'name', label: 'Name' },
    { key: 'org', label: 'Company' },
    { key: 'job_title', label: 'Job title' },
    { key: 'seniority_level', label: 'Seniority' },
    { key: 'department', label: 'Department' },
    { key: 'headline', label: 'Headline' },
    { key: 'summary', label: 'Profile summary' },
    { key: 'location', label: 'Location' },
  ],
};

// Candidates/managers carry big free-text fields (summary = whole-career bio,
// the biggest token sink), so their defaults are deliberately lean — the user
// adds heavier fields consciously and sees the cost in the sample.
const DEFAULT_FIELDS: Record<EntityType, string[]> = {
  companies: ['industry', 'subindustry', 'specialties', 'description'],
  candidates: ['current_position', 'current_position_description'],
  managers: ['job_title', 'org', 'department'],
};

// Above this estimated credit cost, the full run requires an explicit
// confirmation so a large spend can't happen with one accidental click.
const CREDIT_CONFIRM_THRESHOLD = 100;

interface SampleResult {
  total: number;
  sampled: number;
  estimatedCredits: number;
  model: string;
  results: Array<{ id: number; name: string | null; value: any; reasoning: string | null }>;
  sampleTokens?: { input: number; output: number };
  sampleCostUsd?: number;
  estimatedCostGbp?: number;
  byok?: boolean;
  sampleSearches?: number;
  avgSearchesPerRow?: number;
  perRowUsd?: number;
  estimatedFullCostUsd?: number;
}


interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  entityType: EntityType;
  scope: any;             // filter scope snapshot for the current view
  campaignId?: number;    // when set, create a CAMPAIGN-scoped column (over the
                          // campaign's members) instead of a project column
  initialSpec?: any;      // optional prefill (e.g. from an Ada proposal / re-run)
  existingColumnKey?: string; // re-run mode: write back into this column, not a new one
  isAdmin?: boolean;      // show raw token/$ cost (operator only)
  // Called when the background run has been kicked off. The parent owns
  // progress/polling from here so the run continues after the modal closes.
  onJobStarted: (job: { id: number; columnKey: string; columnLabel: string; total: number }) => void;
}

const token = () => localStorage.getItem('token');

const AIColumnModal: React.FC<Props> = ({ isOpen, onClose, projectId, entityType, scope, campaignId, initialSpec, existingColumnKey, isAdmin, onJobStarted }) => {
  // Campaign columns run over candidates/contacts only; map the table's plural
  // entity to the campaign engine's singular type (null = unsupported here).
  const campaignEntity = entityType === 'candidates' ? 'candidate' : entityType === 'managers' ? 'contact' : null;
  const apiBase = campaignId ? `/api/campaigns/${campaignId}/ai-columns` : `/api/ai-columns/${projectId}`;
  const [label, setLabel] = useState('');
  const [outputType, setOutputType] = useState<OutputType>('category');
  const [instruction, setInstruction] = useState('');
  const [fields, setFields] = useState<Set<string>>(new Set(DEFAULT_FIELDS[entityType]));
  const [allowedValues, setAllowedValues] = useState<string[]>(['', '']);
  const [scoreMin, setScoreMin] = useState(1);
  const [scoreMax, setScoreMax] = useState(10);
  const [model, setModel] = useState<'jev' | 'haiku' | 'sonnet'>('haiku');
  const [webResearch, setWebResearch] = useState(false);
  const [webBilling, setWebBilling] = useState<'credits' | 'byok'>('credits');
  const [anthropicConnected, setAnthropicConnected] = useState<boolean | null>(null);

  const [sampling, setSampling] = useState(false);
  const [sample, setSample] = useState<SampleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Learn tab — AI columns lesson.
  useEffect(() => { if (isOpen) tourEvent('aicol-opened'); }, [isOpen]);
  useLessonAction('lesson-aicol-example', () => {
    setLabel('Built a 50MW+ data centre?');
    setOutputType('category');
    setAllowedValues(['Yes', 'No', 'Unclear']);
    setInstruction('Has this person worked on building a data centre larger than 50MW? Read their roles and descriptions — answer Yes, No or Unclear.');
    setSample(null);
  });
  const [confirmingRun, setConfirmingRun] = useState(false);

  // BYOK status — web research runs on the user's own Anthropic key.
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/ai-providers/status', { headers: { 'Authorization': `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        setAnthropicConnected(!!d?.anthropicConnected);
        if (d?.anthropicConnected) setWebBilling('byok');
      })
      .catch(() => setAnthropicConnected(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset / apply prefill whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSample(null);
    setStarting(false);
    setConfirmingRun(false);
    if (initialSpec) {
      setLabel(initialSpec.columnLabel || '');
      setOutputType(initialSpec.outputType || 'category');
      setInstruction(initialSpec.instruction || '');
      setFields(new Set(initialSpec.sourceFields?.length ? initialSpec.sourceFields : DEFAULT_FIELDS[entityType]));
      if (Array.isArray(initialSpec.allowedValues) && initialSpec.allowedValues.length) {
        setAllowedValues([...initialSpec.allowedValues]);
      }
      if (initialSpec.scoreMin != null) setScoreMin(initialSpec.scoreMin);
      if (initialSpec.scoreMax != null) setScoreMax(initialSpec.scoreMax);
      if (initialSpec.model) setModel(initialSpec.model);
    } else {
      setLabel('');
      setOutputType('category');
      setInstruction('');
      setFields(new Set(DEFAULT_FIELDS[entityType]));
      setAllowedValues(['', '']);
      setScoreMin(1);
      setScoreMax(10);
      setModel('haiku');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const cleanAllowed = allowedValues.map(v => v.trim()).filter(Boolean);
  // Campaign columns only support candidates/contacts — not the companies tab.
  const campaignUnsupported = !!campaignId && !campaignEntity;
  const configValid =
    !campaignUnsupported &&
    label.trim() &&
    instruction.trim() &&
    fields.size > 0 &&
    (outputType !== 'category' || cleanAllowed.length >= 2) &&
    (outputType !== 'score' || scoreMax > scoreMin);

  const buildBody = () => ({
    entityType: campaignId ? campaignEntity : entityType,
    columnLabel: label.trim(),
    outputType,
    instruction: instruction.trim(),
    sourceFields: Array.from(fields),
    allowedValues: outputType === 'category' ? cleanAllowed : undefined,
    scoreMin: outputType === 'score' ? scoreMin : undefined,
    scoreMax: outputType === 'score' ? scoreMax : undefined,
    model,
    webResearch,
    webByok: webResearch && webBilling === 'byok' && anthropicConnected === true,
    // Campaign columns run over the whole membership, not a filtered view.
    ...(campaignId ? {} : { scope }),
    ...(existingColumnKey ? { columnKey: existingColumnKey } : {}),
  });

  const toggleField = (key: string) => {
    setFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setSample(null); // config changed — force a fresh preview
  };

  const runSample = async () => {
    setError(null);
    setSampling(true);
    setSample(null);
    setConfirmingRun(false);
    try {
      const res = await fetch(`${apiBase}/sample`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify(buildBody()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setSample(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSampling(false);
    }
  };

  // Kick off the background run and hand it to the parent, which owns progress
  // (a non-blocking pill + live cell fill-in) so the run keeps going after the
  // modal closes — and even if the user navigates away from the project.
  const runFull = async () => {
    setError(null);
    setStarting(true);
    try {
      const res = await fetch(`${apiBase}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify(buildBody()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Run failed');
      onJobStarted({
        id: data.jobId,
        columnKey: data.columnKey,
        columnLabel: label.trim(),
        total: sample?.total || 0,
      });
      onClose();
    } catch (e: any) {
      setError(e.message);
      setStarting(false);
    }
  };

  return (
    <div className="modal-backdrop !z-[70] flex items-center justify-center" onClick={onClose}>
      <div className="modal-dialog max-w-2xl w-full mx-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center justify-between flex-shrink-0 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-violet-600">✨</span>
            <h3 className="text-lg font-semibold text-gray-900">{existingColumnKey ? 'Re-run AI Column' : 'AI Column'}</h3>
            <span className="text-xs text-gray-400 capitalize">· {entityType}</span>
            {campaignId && !campaignUnsupported && <span className="text-xs text-violet-500">· this campaign only</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {campaignUnsupported && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Campaign AI columns run over candidates and contacts only. Switch to the Candidates or Contacts tab to add one.
            </div>
          )}
          {/* ------- Configuration form ------- */}
          <>
            {existingColumnKey && (
              <div className="text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                Re-running into the <strong>same column</strong>, overwriting only the rows that match your
                current filter. Tweak the prompt below and preview to see how many rows will be re-processed.
              </div>
            )}
            <div data-tour="aicol-name">
              <label className="block text-xs font-medium text-gray-600 mb-1">Column name</label>
              <input
                value={label}
                onChange={e => { setLabel(e.target.value); setSample(null); }}
                placeholder="e.g. Actually a restaurant?"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>

            <div data-tour="aicol-output">
              <label className="block text-xs font-medium text-gray-600 mb-1">Output type</label>
              <div className="flex gap-2">
                {(['category', 'text', 'score'] as OutputType[]).map(t => (
                  <button
                    key={t}
                    onClick={() => { setOutputType(t); if (t === 'text' && model === 'jev') setModel('haiku'); setSample(null); }}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border capitalize ${
                      outputType === t ? 'border-violet-500 bg-violet-50 text-violet-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t === 'category' ? 'Category' : t === 'text' ? 'Text' : 'Score'}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {outputType === 'category' && 'Each row gets one value from your list (yes/no is just a 2-value category).'}
                {outputType === 'text' && 'Each row gets a short free-form answer.'}
                {outputType === 'score' && 'Each row gets a number in your range — sortable.'}
              </p>
            </div>

            {outputType === 'category' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Allowed values</label>
                <div className="space-y-1.5">
                  {allowedValues.map((v, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={v}
                        onChange={e => { const next = [...allowedValues]; next[i] = e.target.value; setAllowedValues(next); setSample(null); }}
                        placeholder={`Value ${i + 1}`}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                      />
                      {allowedValues.length > 2 && (
                        <button onClick={() => { setAllowedValues(allowedValues.filter((_, j) => j !== i)); setSample(null); }} className="px-2 text-gray-400 hover:text-red-500">×</button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={() => setAllowedValues([...allowedValues, ''])} className="mt-1.5 text-xs text-violet-600 hover:text-violet-800">+ Add value</button>
              </div>
            )}

            {outputType === 'score' && (
              <div className="flex gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min</label>
                  <input type="number" value={scoreMin} onChange={e => { setScoreMin(parseInt(e.target.value) || 0); setSample(null); }} className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max</label>
                  <input type="number" value={scoreMax} onChange={e => { setScoreMax(parseInt(e.target.value) || 0); setSample(null); }} className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-lg" />
                </div>
              </div>
            )}

            <div data-tour="aicol-instruction">
              <label className="block text-xs font-medium text-gray-600 mb-1">Instruction</label>
              <textarea
                value={instruction}
                onChange={e => { setInstruction(e.target.value); setSample(null); }}
                rows={3}
                placeholder="Describe what to decide for each row, e.g. 'Based on the industry and description, is this company actually a restaurant/food-service business rather than FMCG?'"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>

            <div data-tour="aicol-fields">
              <label className="block text-xs font-medium text-gray-600 mb-1">Fields the AI reads</label>
              <div className="flex flex-wrap gap-1.5">
                {SOURCE_FIELDS[entityType].map(f => (
                  <button
                    key={f.key}
                    onClick={() => toggleField(f.key)}
                    className={`px-2.5 py-1 text-xs rounded-full border ${
                      fields.has(f.key) ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div data-tour="aicol-model">
              <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
              {/* The typed-judgment engine ('jev') is parked for AI columns: the
                  feature's core deliverable is the written reasoning per row,
                  which judgments don't produce. The backend engine remains
                  (and still powers Job Spec Match); restore a picker button
                  here to re-enable it for columns. */}
              <div className="flex gap-2">
                <button onClick={() => { setModel('haiku'); setSample(null); }} className={`flex-1 px-3 py-2 text-sm rounded-lg border ${model === 'haiku' ? 'border-violet-500 bg-violet-50 text-violet-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  Haiku · fast &amp; cheap
                </button>
                <button onClick={() => { setModel('sonnet'); setSample(null); }} className={`flex-1 px-3 py-2 text-sm rounded-lg border ${model === 'sonnet' ? 'border-violet-500 bg-violet-50 text-violet-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  Sonnet · nuanced
                </button>
              </div>
            </div>

            {/* Web research */}
            <div className={`rounded-lg border px-3 py-2.5 ${webResearch ? 'border-sky-300 bg-sky-50' : 'border-gray-200'}`}>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={webResearch}
                  onChange={e => { setWebResearch(e.target.checked); if (e.target.checked && model === 'jev') setModel('haiku'); setSample(null); }}
                />
                <span>
                  <span className="text-sm font-medium text-gray-800">🌐 Web research</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Claude searches the web (up to 4 searches per row) to answer from live information, with
                    sources and a confidence rating on every cell.
                  </span>
                </span>
              </label>
              {webResearch && (
                <div className="mt-2.5 ml-6 space-y-1.5">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={webBilling === 'credits'}
                      onChange={() => { setWebBilling('credits'); setSample(null); }}
                    />
                    <span className="text-xs text-gray-600">
                      <span className="font-medium text-gray-800">Use MarketMapr credits</span> — billed like any AI
                      column; the preview shows the credit estimate before you commit.
                    </span>
                  </label>
                  <label className={`flex items-start gap-2 ${anthropicConnected === false ? 'opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={webBilling === 'byok'}
                      disabled={anthropicConnected === false}
                      onChange={() => { setWebBilling('byok'); setSample(null); }}
                    />
                    <span className="text-xs text-gray-600">
                      <span className="font-medium text-gray-800">Use my own Anthropic API key</span> — no credits used;
                      token &amp; search spend goes to your Anthropic account.
                      {anthropicConnected === false && (
                        <span className="block text-amber-600 mt-0.5">
                          Connect your key in Settings → Integrations → AI to enable this option.
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              )}
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          </>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            data-tour="aicol-preview"
            onClick={runSample}
            disabled={!configValid || sampling}
            className="px-4 py-1.5 text-sm text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sampling ? 'Previewing…' : webResearch ? (webBilling === 'byok' ? 'Preview 5 rows (uses your key)' : 'Preview 5 rows') : 'Preview 10 rows'}
          </button>
        </div>
      </div>

      {/* Preview overlay — a clear, separate popup for the 10-row dry-run */}
      {sample && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4" onClick={e => e.stopPropagation()}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span className="text-violet-600">✨</span> Preview
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Sample of {sample.sampled} · full run would process <strong>{sample.total.toLocaleString()}</strong> rows
                </p>
              </div>
              <div className="text-right">
                {sample.byok ? (
                  <>
                    <div className="text-sm font-semibold text-gray-900">≈ ${sample.estimatedFullCostUsd?.toFixed(2)} on your key</div>
                    <div className="text-[11px] text-gray-400">
                      ~${sample.perRowUsd?.toFixed(4)}/row · avg {sample.avgSearchesPerRow?.toFixed(1)} searches/row · estimate
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-gray-900">~{sample.estimatedCredits} credits</div>
                    <div className="text-[11px] text-gray-400">
                      {sample.estimatedCostGbp != null ? `≈ £${sample.estimatedCostGbp.toFixed(2)} · ` : ''}estimated
                    </div>
                  </>
                )}
              </div>
            </div>

            {isAdmin && sample.sampleTokens && (
              <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800 flex items-center justify-between">
                <span>
                  Sample: {sample.sampleTokens.input.toLocaleString()} in + {sample.sampleTokens.output.toLocaleString()} out tokens
                  {' '}≈ ${sample.sampleCostUsd?.toFixed(4)}
                </span>
                <span className="font-medium">
                  Full run cost ≈ ${sample.estimatedFullCostUsd?.toFixed(2)}
                </span>
              </div>
            )}

            <div className="px-4 py-2 overflow-y-auto divide-y divide-gray-100">
              {sample.results.map(r => (
                <div key={r.id} className="px-2 py-2.5 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-700 truncate">{r.name || `#${r.id}`}</span>
                    <span className="font-semibold text-violet-700 whitespace-nowrap">{r.value == null ? '—' : String(r.value)}</span>
                  </div>
                  {r.reasoning && <div className="text-xs text-gray-400 mt-1">{r.reasoning}</div>}
                  {(r as any).confidence && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${(r as any).confidence === 'high' ? 'bg-green-50 text-green-700' : (r as any).confidence === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                        {(r as any).confidence} confidence
                      </span>
                      {((r as any).sources || []).slice(0, 3).map((src: any, i: number) => (
                        <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline truncate max-w-[160px]">
                          {src.title || src.url}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {confirmingRun ? (
              <div className="px-6 py-3 border-t border-amber-100 bg-amber-50 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-amber-800">
                  {sample.byok
                    ? <>This run costs an estimated <strong>${sample.estimatedFullCostUsd?.toFixed(2)}</strong> on your Anthropic key (no credits). Go ahead?</>
                    : <>This run will use about <strong>{sample.estimatedCredits} credits</strong>{sample.estimatedCostGbp != null && <> (≈ £{sample.estimatedCostGbp.toFixed(2)})</>}. Go ahead?</>}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmingRun(false)} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 bg-white">
                    Back
                  </button>
                  <button
                    onClick={runFull}
                    disabled={starting}
                    className="px-4 py-1.5 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {starting ? 'Starting…' : sample.byok ? `Yes, run (≈$${sample.estimatedFullCostUsd?.toFixed(2)})` : `Yes, run (~${sample.estimatedCredits} credits${sample.estimatedCostGbp != null ? ` · £${sample.estimatedCostGbp.toFixed(2)}` : ''})`}
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-gray-400">Not happy? Adjust the instruction or fields and preview again.</span>
                <div className="flex gap-2">
                  <button onClick={() => setSample(null)} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Adjust
                  </button>
                  <button
                    onClick={() => { if (sample.estimatedCredits > CREDIT_CONFIRM_THRESHOLD) setConfirmingRun(true); else runFull(); }}
                    disabled={sample.total === 0 || starting}
                    className="px-4 py-1.5 text-sm text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {starting ? 'Starting…' : `Run on ${sample.total.toLocaleString()} rows`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIColumnModal;
